import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import SeoFields, { inputCls, labelCls, emptySeo, slugify } from "./SeoFields";
import { Plus, Pencil, Trash2, Loader2, X, Save, ExternalLink } from "lucide-react";

const blank = { title: "", slug: "", excerpt: "", content: "", coverImage: "", author: "DaddyAnna Team", tags: [], status: "published", seo: { ...emptySeo } };

const BlogManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/blog"); setItems(data); }
    catch (e) { toast({ title: "Failed to load posts", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...blank, seo: { ...emptySeo }, tags: [] });
  const startEdit = (p) => setEditing(JSON.parse(JSON.stringify(p)));

  const save = async () => {
    if (!editing.title || !editing.slug) { toast({ title: "Title and slug required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing.id) { await api.put(`/admin/blog/${editing.id}`, editing); toast({ title: "Post updated" }); }
      else { await api.post("/admin/blog", editing); toast({ title: "Post created" }); }
      setEditing(null); load();
    } catch (e) { toast({ title: e.response?.data?.detail || "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); toast({ title: "Deleted" }); load(); }
    catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  if (editing) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-white">{editing.id ? "Edit Post" : "New Post"}</h2>
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={22} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })} />
          </div>
          <div>
            <label className={labelCls}>Slug (URL)</label>
            <input className={inputCls} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Author</label>
            <input className={inputCls} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Cover Image URL</label>
            <input className={inputCls} value={editing.coverImage} onChange={(e) => setEditing({ ...editing, coverImage: e.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Excerpt</label>
          <textarea rows={2} className={inputCls} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Content (HTML supported)</label>
          <textarea rows={10} className={inputCls} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="<p>Write your post...</p>" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tags (comma separated)</label>
            <input className={inputCls} value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="cricket id, guide" />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
        <SeoFields seo={editing.seo} onChange={(seo) => setEditing({ ...editing, seo })} />
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="btn-neon inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Post
          </button>
          <button onClick={() => setEditing(null)} className="px-6 py-2.5 rounded-lg border border-white/15 text-gray-300 hover:bg-white/5">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-white">Blog Posts</h2>
        <button onClick={startNew} className="btn-neon inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm"><Plus size={18} /> New Post</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00ff87]" size={32} /></div>
      ) : items.length === 0 ? (
        <p className="text-gray-400 py-10 text-center">No posts yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#0e0f1a] p-4">
              <div className="flex items-center gap-3 min-w-0">
                {p.coverImage && <img src={p.coverImage} alt="" className="h-11 w-16 rounded object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white truncate">{p.title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-[#00ff87]/15 text-[#00ff87]" : "bg-yellow-500/15 text-yellow-500"}`}>{p.status}</span>
                  </div>
                  <span className="text-xs text-gray-500">/blog/{p.slug}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-[#00e5ff]"><ExternalLink size={16} /></a>
                <button onClick={() => startEdit(p)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-[#00ff87]"><Pencil size={16} /></button>
                <button onClick={() => remove(p)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManager;
